import {HocuspocusProvider, HocuspocusProviderWebsocket} from '@hocuspocus/provider';
import {collaborationLog, fastPredictableHash} from './collaboration.utils';
import {YMapEvent} from 'yjs';
import {
  CE_CONNECT_INITIATED,
  CE_OPEN,
  CE_CONNECT,
  CE_AUTHENTICATED,
  CE_AUTHENTICATION_FAILED,
  CE_STATUS,
  CE_MESSAGE,
  CE_OUTGOING_MESSAGE,
  CE_NEW_SERVER_STATE,
  CE_CLOSE,
  CE_DISCONNECT,
  CE_DESTROY,
  CE_AWARENESS_CHANGE,
  CE_STATELESS,
  CE_CONNECT_STARTED,
  CE_COLLABORATION_WEB_SOCKET_CREATED,
  CE_UNSYNCED_CHANGES,
  TIMEOUTS,
  CE_NEW_LOCK_STATE,
} from './types';
import {FirehoseHandle} from './FirehoseHandle';

// using relative urls for websockets is only as of
// 2024 in many browsers, so use document href.
// web workers don't have a window, so use self
export const ENDPOINT_COLLABORATION_URL = `${
  self.location.protocol === 'https:' ? 'wss' : 'ws'
}://${self.location.host}/api/v2/collaboration`;

export const YDOC_COLLABORATION_KEY = 'collaborationDocument';
export const YDOC_LOCK_KEY = 'lockInfo';

/**
 * Reponsible for One Hocuspocus Instance
 *
 * We are using YJS as a merging tool, but when we
 * are offline we cannot use YJS as a merging tool.
 *
 * YJS works on lamport timestamps per field, and server will
 * only use json as source of truth which doesn't have
 * the timestamps on each field. this means whenever
 * the collaboration server loads the document into
 * memory, the history is wiped out and we don't know
 * how to merge edits anymore.
 *
 * Hocuspocus does not support this usecase, so we
 * just work around hocuspocus handling this usecase
 * by isolating hocuspocus to just one active websocket
 * connection, and we make a whole new hocuspocus
 * instance every time the connection goes down.
 */
export class CollaborationWebSocket {
  /**
   *
   * @param {Subject} firehose the event bus
   * @param {string} name the document name
   */
  constructor(firehose, name) {
    this.fh = new FirehoseHandle(firehose, name);
    this.hasConnectAttempt = false;
    this.shouldConnect = false;
    this.name = name;
    this._websocketProvider = null;
    this._provider = null;
    this.messageTimeout = TIMEOUTS.maxWaitForServerMessage;
    this.fh.sendEvent(CE_COLLABORATION_WEB_SOCKET_CREATED);
    this.maxOpen = TIMEOUTS.maxWebsocketOpen;
    this.maxWaitForSync = TIMEOUTS.maxWaitForSync;
    this.connectTimeout = TIMEOUTS.connectTimeout;
  }

  connect({token, localAwarenessState}) {
    if (this.hasConnectAttempt) {
      this.fh.sendError('connect called twice for one wrapper');
      return;
    }
    this.hasConnectAttempt = true;
    this.hasSynced = false;
    const url = `${ENDPOINT_COLLABORATION_URL}/${fastPredictableHash(this.name)}`;
    this.shouldConnect = true;
    this.fh.sendEvent(CE_CONNECT_INITIATED);
    this._myCurrentState = {};
    this.lastUnsyncedChanges = 0;
    let cws = this;

    const LoggingWebSocketPolyfill = function (...args) {
      collaborationLog(
        `TRACE: ${cws.name} ${
          cws.fh?.instanceId
        } WebSocket network attempt ${JSON.stringify(args)}`
      );
      return new WebSocket(...args);
    };
    this._websocketProvider = new HocuspocusProviderWebsocket({
      url,
      messageReconnectTimeout: this.messageTimeout,
      maxAttempts: 1,
      WebSocketPolyfill: LoggingWebSocketPolyfill,
    });
    this.openedAt = Date.now();
    this.networkConnected = false;
    this.connectTimeoutId = null;
    this._provider = new HocuspocusProvider({
      name: cws.name,
      websocketProvider: cws._websocketProvider,
      token,
      broadcast: false, // do not sync between tabs
      onOpen() {
        cws.networkConnected = true;
        collaborationLog(`TRACE: ${cws.name} ${cws.fh?.instanceId} onOpen`); // onOpen is called twice.
        cws.fh.sendEvent(CE_OPEN);
      },
      onConnect() {
        collaborationLog(`TRACE: ${cws.name} ${cws.fh?.instanceId} onConnect`);
        cws.fh.sendEvent(CE_CONNECT);
      },
      onAuthenticated() {
        cws.fh.sendEvent(CE_AUTHENTICATED);
      },
      onAuthenticationFailed: ({reason}) => {
        collaborationLog(
          `TRACE: ${cws.name} ${cws.fh?.instanceId} onAuthenticationFailed`
        );
        cws.fh.sendEvent(CE_AUTHENTICATION_FAILED, reason);
      },
      onStatus: ({status}) => {
        if (!cws.connectTimeoutId && status === 'connecting') {
          cws.connectTimeoutId = setTimeout(() => {
            if (cws.shouldConnect && !cws.networkConnected) {
              collaborationLog(
                `ERROR: ${cws.name} ${cws.fh?.instanceId} network connection timeout after ${cws.connectTimeout}ms`
              );
              cws.fh.sendError(`network connection timeout`);
            }
          }, cws.connectTimeout);
        }
        collaborationLog(`TRACE: ${cws.name} ${cws.fh?.instanceId} onStatus ${status}`);
        cws.fh.sendEvent(CE_STATUS, status);
      },
      onMessage: (message) => {
        cws.fh.sendEvent(CE_MESSAGE, message);
      },
      onOutgoingMessage: (message) => {
        cws.fh.sendEvent(CE_OUTGOING_MESSAGE, message);
        if (!cws.shouldConnect) return;
        if (cws.maxOpen && Date.now() - cws.openedAt > cws.maxOpen) {
          collaborationLog(
            `WARN: ${cws.name} ${cws.fh?.instanceId} outgoing message on a websocket that is too old`
          );
          cws.fh.sendError('outgoing message on a websocket that is too old');
        }
      },
      onClose: ({event}) => {
        cws.fh.sendEvent(CE_CLOSE, event);
        if (cws.shouldConnect) {
          cws.fh.sendError('network error: active websocket closed');
        }
      },
      onDisconnect: ({event}) => {
        cws.fh.sendEvent(CE_DISCONNECT, event);
      },
      onDestroy() {
        cws.fh.sendEvent(CE_DESTROY);
      },
      onStateless: ({payload}) => {
        cws.fh.sendEvent(CE_STATELESS, payload);
      },
      // https://github.com/ueberdosis/hocuspocus/blob/4defd269217435848dd582cdaab60b386056c95c/packages/provider/src/HocuspocusProvider.ts#L302
      // https://github.com/ueberdosis/hocuspocus/blob/4defd269217435848dd582cdaab60b386056c95c/packages/provider/src/HocuspocusProvider.ts#L360
      onSynced: ({state}) => {
        if (!cws.shouldConnect) return;
        if (!state) {
          cws.fh.sendError('synced called with bad state');
          return;
        }
        if (cws.hasSynced) {
          // ignore repeated synced events
          return;
        }
        const ymap = cws._provider.document.getMap(YDOC_COLLABORATION_KEY);
        ymap.observe((event) => cws._ymapObserve(event));
        const lockInfo = cws._provider.document.getMap(YDOC_LOCK_KEY);
        lockInfo.observe((event) => cws._lockInfoObserve(event));
        cws.hasSynced = true;
      },
      onAwarenessChange: () => {
        if (!cws.shouldConnect) return;
        let remotes = [];
        let awarenessStates = cws._provider.awareness.getStates();
        for (const [clientID, awarenessState] of awarenessStates.entries()) {
          remotes.push({...awarenessState, clientID});
        }
        remotes.sort((a, b) => a.clientID - b.clientID);
        cws.fh.sendEvent(CE_AWARENESS_CHANGE, {remotes});
      },
    });
    this._provider.on('unsyncedChanges', (unsyncedChanges) => {
      if (!cws.shouldConnect) return;
      if (unsyncedChanges > 0 && !cws.unsyncedTimeout) {
        cws.unsyncedTimeout = setTimeout(() => {
          if (cws.shouldConnect) {
            cws.fh.sendError('timeout while waiting for changes to sync');
          }
        }, cws.maxWaitForSync);
      }
      if (unsyncedChanges === 0 && cws.unsyncedTimeout) {
        clearTimeout(cws.unsyncedTimeout);
        cws.unsyncedTimeout = null;
      }
      if (cws.hasSynced && unsyncedChanges === 0 && cws.lastUnsyncedChanges !== 0) {
        // send a server update with our current state, now that everything is saved
        const ymap = cws._provider.document.getMap(YDOC_COLLABORATION_KEY);
        cws.fh.sendEvent(CE_NEW_SERVER_STATE, ymap.toJSON());

        const lockInfo = cws._provider.document.getMap(YDOC_LOCK_KEY);
        cws.fh.sendEvent(CE_NEW_LOCK_STATE, lockInfo.toJSON());
      }
      cws.lastUnsyncedChanges = unsyncedChanges;
      cws.fh.sendEvent(CE_UNSYNCED_CHANGES, unsyncedChanges);
    });
    this._provider.awareness.setLocalState(localAwarenessState);
    this.fh.sendEvent(CE_CONNECT_STARTED);
  }

  /**
   *
   * @param {YMapEvent<any>} ymapEvent
   * @returns
   */
  _ymapObserve(ymapEvent) {
    const local = ymapEvent?.transaction?.local;
    if (local) return;
    const ymap = ymapEvent?.currentTarget;
    if (!ymap) {
      this.fh.sendError('Missing ymap from observe event');
      return;
    }
    if (this.lastUnsyncedChanges === 0) {
      this.fh.sendEvent(CE_NEW_SERVER_STATE, ymap.toJSON());
    }
  }

  /**
   *
   * @param {YMapEvent<any>} ymapEvent
   * @returns
   */
  _lockInfoObserve(ymapEvent) {
    const lockInfo = ymapEvent?.currentTarget;
    if (!lockInfo) {
      this.fh.sendError('Missing lockInfo from observe event');
      return;
    }
    this.fh.sendEvent(CE_NEW_LOCK_STATE, lockInfo.toJSON());
  }

  close() {
    collaborationLog(
      `TRACE: ${this.name} ${this.fh?.instanceId}  -- CollaborationWebSocket#close called`
    );
    this.shouldConnect = false;
    this._provider?.destroy();
    this._websocketProvider?.destroy();
    this.fh.close();
    this.unsyncedTimeout && clearTimeout(this.unsyncedTimeout);
    this.unsyncedTimeout = null;
  }

  /**
   * Processes a flat object where each value is either a number, string, null, or explicitly undefined.
   *
   * @param {Object.<string, (number|string|null|undefined)>} diffResults - The input object to process
   *
   * @example
   * // Valid input examples: (this will modify keys name, age, title; and remove key department)
   * {
   *   name: "John",
   *   age: 30,
   *   title: null,
   *   department: undefined,
   * }
   *
   */
  localUpdated(diffResults) {
    if (!this.hasSynced || !this.shouldConnect) {
      this.fh.sendError('localUpdated called on a closed websocket');
      return;
    }
    if (this.maxOpen && Date.now() - this.openedAt > this.maxOpen) {
      this.fh.sendError('local updated on a websocket that is too old');
      return;
    }
    const ydoc = this._provider.document;
    const keys = Object.keys(diffResults);
    ydoc.transact(() => {
      const ymap = ydoc.getMap(YDOC_COLLABORATION_KEY);
      for (const key of keys) {
        const value = diffResults[key];
        if (value === undefined) {
          ymap.delete(key);
        } else {
          ymap.set(key, value);
        }
      }
    });
  }

  setLocalAwarenessState(localAwarenessState) {
    this._provider?.awareness?.setLocalState(localAwarenessState);
    if (this.maxOpen && Date.now() - this.openedAt > this.maxOpen) {
      this.fh.sendError('awareness updated on a websocket that is too old');
    }
  }

  sendStateless(message) {
    if (this.shouldConnect) this._provider.sendStateless(message);
  }
}
